import React, { useState,useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { upsertShippingSettings } from "../../../../services/vendor/vendor.service"; // make sure path matches your project

const FuelRateTable = ({
  initialData,
  setFuelRate,
  nationalCubeRate,
  nationalSurcharge,
}) => {
  //   console.log("fuelData", initialData);
  const [data, setData] = useState(initialData);
  // 🟢 Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [nationalAvg, setNationalAvg] = useState(nationalCubeRate || "");
  const [surchargePercent, setSurchargePercent] = useState(nationalSurcharge || "");

 useEffect(() => {
   if (nationalCubeRate !== undefined && nationalSurcharge !== undefined) {
     setNationalAvg(String(nationalCubeRate));
     setSurchargePercent(`${nationalSurcharge}%`);
   }
 }, [nationalCubeRate, nationalSurcharge]);


  const handleNationalAvgChange = (e) => {
    const input = e.target.value;
    setNationalAvg(input); // Always update the UI

    const num = parseFloat(input);
    if (isNaN(num)) {
      setSurchargePercent("");
      return;
    }

    const match = data.find(
      (row) => num >= parseFloat(row.from) && num <= parseFloat(row.to)
    );

    if (match) {
      setSurchargePercent(`${match.surcharge}%`);
    } else {
      setSnackbar({
        open: true,
        message: "Not found",
        severity: "error",
      });
      setSurchargePercent("");
    }
  };

  const handleSurchargeChange = (e) => {
    const input = e.target.value.replace("%", "");
    setSurchargePercent(`${input}%`);

    const num = parseFloat(input);
    if (isNaN(num)) {
      setNationalAvg("");
      return;
    }

    const match = data.find((row) => Number(row.surcharge) === num);
    if (match) {
      const avg = ((parseFloat(match.from) + parseFloat(match.to)) / 2).toFixed(
        2
      );
      setNationalAvg(avg);
    } else {
      setSnackbar({
        open: true,
        message: "Not found",
        severity: "error",
      });
      setNationalAvg("");
    }
  };

  const handleInputChange = (e, index, field) => {
    const newData = [...data];
    newData[index][field] = e.target.value;
    setData(newData);
  };

  const handleSave = async () => {
    try {
      const payload = {
        feulRates: data.map((row) => ({
          from: Number(row.from),
          to: Number(row.to),
          surcharge: Number(row.surcharge),
        })),
        nationalCubeRate: parseFloat(nationalAvg) || 0,
        nationalSurcharge: parseFloat(surchargePercent) || 0,
      };

      //   console.log("Sending payload:", payload);

      const res = await upsertShippingSettings(payload);
      if (res?.data) {
        setData(res?.data?.feulRates);
      }
      // 🟢 Success snackbar
      setSnackbar({
        open: true,
        message: res?.message || "Fuel rate table saved successfully!",
        severity: "success",
      });
      setFuelRate(false);
    } catch (error) {
      // 🔴 Error snackbar
      setSnackbar({
        open: true,
        message: "Failed to save fuel rates.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ width: "100%", fontFamily: "sans-serif", pb: 2 }}>
      {/* 🔔 Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          display: "flex",
          position: "sticky",
          top: 0,
          p: 2,
          zIndex: 999,
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f0f0f0",
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          Fuel Rate Table
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#17a858",
            color: "white",
            fontWeight: "bold",
            borderRadius: "50px",
            width: "30%",
            "&:hover": {
              backgroundColor: "#17a858",
            },
          }}
          onClick={handleSave}
        >
          save
        </Button>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={4}
        sx={{
          fontFamily: "inherit",
          mt: 2,
          px: 1.5,
          py: 0.5,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          National average:
        </Typography>

        <TextField
          variant="outlined"
          size="small"
          value={nationalAvg}
          onChange={handleNationalAvgChange}
          InputProps={{
            sx: { borderRadius: "999px", width: 80, textAlign: "center" },
          }}
        />

        <ArrowForwardIcon sx={{ fontSize: 18 }} />

        <TextField
          variant="outlined"
          size="small"
          value={surchargePercent}
          onChange={handleSurchargeChange}
          InputProps={{
            sx: { borderRadius: "999px", width: 80, textAlign: "center" },
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 0,
          mt: 0,
          maxHeight: "100%",
          overflowY: "auto",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead style={{ backgroundColor: "transparent" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Fuel Rate: from</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                Fuel Rate: to
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                Surcharge %
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow
                key={index}
                sx={{
                  backgroundColor: index % 2 === 0 ? "#f0f0f0" : "#f0f0f0",
                }}
              >
                <TableCell component="th" scope="row">
                  <TextField
                    variant="standard"
                    placeholder="enter"
                    value={row.from}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val)) {
                        handleInputChange(e, index, "from");
                      }
                    }}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      "& .MuiInput-input": { p: 0 },
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    variant="standard"
                    placeholder="enter"
                    value={row.to}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val)) {
                        handleInputChange(e, index, "to");
                      }
                    }}
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      "& .MuiInput-input": { p: 0 },
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextField
                    variant="standard"
                    placeholder="enter"
                    value={row.surcharge}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*\.?\d*$/.test(val)) {
                        handleInputChange(e, index, "surcharge");
                      }
                    }}
                    inputProps={{ style: { textAlign: "center" } }}
                    InputProps={{ disableUnderline: true }}
                    sx={{
                      "& .MuiInput-input": { p: 0 },
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FuelRateTable;
